import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useMemo, VFC } from "react";
import styled from "styled-components";
import { Markdown, Select } from "components";
import { useFormField, useAnchor } from "hooks";
import { ComponentIds } from "types/enums/componentIds";
import { PageContent, StatePlanInfoContent } from "types/graphql/generated";
interface Props {
  content: StatePlanInfoContent & { statePlans: PageContent["statePlans"] };
  className?: string;
}

const StatePlanInfo: VFC<Props> = withErrorBoundary(
  ({ content: { title, selectLabel, statePlanInfoDescription, statePlans }, className = "" }) => {
    const stateDescription = useFormField();

    const stateDetails = useMemo(() => {
      return statePlans.data.find((statePlan) => statePlan.attributes.state === stateDescription.value)?.attributes
        .stateInfo;
    }, [stateDescription.value, statePlans.data]);

    useAnchor("state-plan-info");
    useAnchor(ComponentIds.StatePlanFinder);

    return (
      <StyledStatePlanInfo className={`state-plan-info ${ComponentIds.StatePlanFinder}`}>
        <div className={`${className}StatePlanInfo Content`}>
          <div className="StatePlanInfo__textContent">
            <Markdown className="StatePlanInfo__title Color_primary Font_48_54_600 md:Font_40_48_600">{title}</Markdown>
            {statePlanInfoDescription && (
              <Markdown className="StatePlanInfo__description Color_secondary Font_24_32_450">
                {statePlanInfoDescription}
              </Markdown>
            )}
          </div>

          <div className="StatePlanInfo__stateContainer">
            <div className="StatePlanInfo__selectContainer">
              <Select value={stateDescription.value} onChange={stateDescription.onChange} id={selectLabel}>
                <option value="" disabled>
                  {selectLabel}
                </option>
                {statePlans.data.map(({ attributes: { state } }, index) => (
                  <option key={index}>{state}</option>
                ))}
              </Select>
            </div>
            {stateDescription.value !== stateDescription.initialValue ? (
              <div>
                <p className="StatePlanInfo__selectValue Color_primary Font_24_32_600">{stateDescription.value}</p>
                <Markdown className="StatePlanInfo__stateDetails Color_secondary Font_18_24_450">
                  {stateDetails ?? ""}
                </Markdown>
              </div>
            ) : null}
          </div>
        </div>
      </StyledStatePlanInfo>
    );
  },
  {}
);

const StyledStatePlanInfo = styled.section`
  background: ${({ theme }) => theme.colors.coral["05"]};
  margin-top: -64px;
  padding-top: 64px;

  .StatePlanInfo {
    display: flex;
    justify-content: space-between;
  }

  .StatePlanInfo__textContent {
    max-width: 100%;
    width: 440px;
  }

  .StatePlanInfo__title {
    position: relative;
  }

  .StatePlanInfo__description {
    margin-top: 24px;
  }

  .StatePlanInfo__stateContainer {
    flex: 1;
    margin-left: 64px;
  }

  .StatePlanInfo__selectContainer {
    max-width: 400px;
  }

  .StatePlanInfo__selectValue {
    margin-top: 32px;
  }

  .StatePlanInfo__stateDetails {
    margin-top: 16px;

    p:not(:first-child) {
      margin-top: 24px;
    }

    strong {
      font-weight: 600;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .StatePlanInfo__stateContainer {
      margin-left: 40px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .StatePlanInfo {
      flex-direction: column;
    }

    .StatePlanInfo__description {
      max-width: unset;
    }

    .StatePlanInfo__stateContainer {
      margin-left: 0;
      margin-top: 40px;
    }

    .StatePlanInfo__selectContainer {
      margin-top: 16px;
    }
  }
`;

gql`
  fragment StatePlanInfoContent on ComponentStatePlanInfoStatePlanInfo {
    title
    selectLabel
    statePlanInfoDescription
  }
`;

export { StatePlanInfo };
export type { Props as StatePlanInfoProps };
